import React from "react";
import { BarChart3, AlertTriangle, TrendingUp, Globe, Shield } from "lucide-react";
import DataPanel from "../DataPanel";
import EmptyState from "../EmptyState";
import ActionPlanIssuesPanel from "../ActionPlanIssuesPanel";
import WidgetExportBtn from "../../reports/WidgetExportBtn";
import { flattenKeywords, flattenTechnicalIssues, flattenTrafficHistory, flattenMetrics } from "../../reports/exportDataUtils";

interface OverviewTabContentProps {
  liveData?: any | null;
  activeSubNav?: string;
  cardId?: string;
}

const severityColor = (sev: string) => {
  const normalized = (sev || "").toUpperCase();
  if (normalized === "HIGH") return { bg: "rgba(248,113,113,0.15)", text: "#F87171", border: "rgba(248,113,113,0.3)" };
  if (normalized === "LOW") return { bg: "rgba(74,222,128,0.15)", text: "#4ADE80", border: "rgba(74,222,128,0.3)" };
  return { bg: "rgba(255,154,74,0.15)", text: "#FF9A4A", border: "rgba(255,154,74,0.3)" };
};

const MetricCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
    <div className="text-xs mb-1" style={{ color: "#9AA0B3" }}>{label}</div>
    <div className="text-xl font-bold" style={{ color: highlight ? "#FF9A4A" : "#E8E4FF" }}>{value}</div>
  </div>
);

const OverviewTabContent: React.FC<OverviewTabContentProps> = ({ liveData, activeSubNav, cardId = "search-intelligence" }) => {
  // ─── SUB-NAV: Traffic & Trends ───
  if (activeSubNav === "traffic") {
    const current = liveData?.currentMetrics;
    const history: any[] = liveData?.history ?? [];
    const worldwide: any[] = liveData?.worldwideOverview ?? [];
    const hasData = current || history.length > 0;

    return (
      <div className="space-y-6">
        <DataPanel title="Current Traffic Metrics" icon={<TrendingUp className="w-4 h-4" />} actions={<WidgetExportBtn title="Current Traffic Metrics" data={current ? flattenMetrics(current) : []} />}>
          {hasData && current ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricCard label="Organic Traffic" value={(current.organicTraffic ?? 0).toLocaleString()} highlight />
              <MetricCard label="Organic Keywords" value={(current.organicKeywords ?? 0).toLocaleString()} />
              <MetricCard label="Organic Cost" value={`$${(current.organicCost ?? 0).toLocaleString()}`} />
              <MetricCard label="Top 1–5" value={(current.top1_5 ?? 0).toLocaleString()} />
              <MetricCard label="Top 6–10" value={(current.top6_10 ?? 0).toLocaleString()} />
              <MetricCard label="Top 11–20" value={(current.top11_20 ?? 0).toLocaleString()} />
            </div>
          ) : (
            <EmptyState title="No traffic data yet" description="Run a Traffic & Trends audit to see your current traffic metrics." />
          )}
        </DataPanel>

        <DataPanel title="Traffic History" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Traffic History" data={flattenTrafficHistory(history)} />}>
          {history.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                      {["Period", "Traffic", "Keywords", "Top 1-5", "Top 6-10", "Top 11-20"].map((h) => (
                        <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice(0, 10).map((h: any, i: number) => (
                      <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                        <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{h.month}/{h.year}</td>
                        <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{(h.trafficSum ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(h.keywordsCount ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2" style={{ color: "#4ADE80" }}>{(h.top1_5 ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2" style={{ color: "#8B7BF7" }}>{(h.top6_10 ?? 0).toLocaleString()}</td>
                        <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{(h.top11_20 ?? 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
                Showing {Math.min(history.length, 10)} of {history.length} months
              </div>
            </>
          ) : (
            <EmptyState title="No history data yet" description="Run a Traffic & Trends audit to see historical traffic data." />
          )}
        </DataPanel>

        <DataPanel title="Worldwide Traffic Overview" icon={<Globe className="w-4 h-4" />} actions={<WidgetExportBtn title="Worldwide Traffic Overview" data={worldwide.map(w => ({ Region: w.source, Traffic: w.traffic, Keywords: w.keywords, "Est. Cost": w.price }))} />}>
          {worldwide.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Region", "Traffic", "Keywords", "Est. Cost"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {worldwide.map((w: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2 font-medium uppercase" style={{ color: "#8B7BF7" }}>{w.source}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{(w.traffic ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(w.keywords ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>${(w.price ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No worldwide data yet" description="Run a Traffic & Trends audit to see regional traffic breakdown." />
          )}
        </DataPanel>
      </div>
    );
  }

  // ─── SUB-NAV: Authority ───
  if (activeSubNav === "authority") {
    const bl = liveData?.backlinks;
    const da = bl?.domainAuthority;
    const anchors: any[] = bl?.anchors ?? [];
    const topSources: any[] = bl?.topSources ?? [];
    const hasData = da || topSources.length > 0;

    return (
      <div className="space-y-6">
        <DataPanel title="Domain Authority & Trust" icon={<Shield className="w-4 h-4" />} actions={<WidgetExportBtn title="Authority & Trust" data={da ? flattenMetrics({ "Domain Authority": da.domainAuthority, "Page Authority": da.pageAuthority, "Total Backlinks": bl?.total, "Referring Domains": bl?.referringDomains, "Anchor Diversity": bl?.anchorDiversity, "Top Sources": topSources.length }) : []} />}>
          {hasData ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricCard label="Domain Authority" value={`${da?.domainAuthority ?? liveData?.domainMetrics?.authorityScore ?? 0}/100`} highlight />
              <MetricCard label="Page Authority" value={`${da?.pageAuthority ?? 0}/100`} />
              <MetricCard label="Total Backlinks" value={(bl?.total ?? liveData?.domainMetrics?.backlinks ?? 0).toLocaleString()} />
              <MetricCard label="Referring Domains" value={(bl?.referringDomains ?? liveData?.domainMetrics?.referringDomains ?? 0).toLocaleString()} />
              <MetricCard label="Anchor Diversity" value={(bl?.anchorDiversity ?? 0).toLocaleString()} />
              <MetricCard label="Top Sources" value={String(topSources.length)} />
            </div>
          ) : (
            <EmptyState title="No authority data yet" description="Run an Authority & Trust audit to see your domain authority metrics." />
          )}
        </DataPanel>

        <DataPanel title="Top Referring Domains" icon={<TrendingUp className="w-4 h-4" />} actions={<WidgetExportBtn title="Top Referring Domains" data={topSources.map(s => ({ Domain: s.domain, Backlinks: s.backlinks, DA: s.domainAuthority }))} />}>
          {topSources.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Domain", "Backlinks", "Domain Authority"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topSources.slice(0, 10).map((s: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2 font-mono text-xs" style={{ color: "#8B7BF7" }}>{s.domain}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{s.backlinks}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{s.domainAuthority ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No referring domain data yet" description="Run an Authority & Trust audit to see your top referring domains." />
          )}
        </DataPanel>

        <DataPanel title="Anchor Text Distribution" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Anchor Text Distribution" data={anchors.map(a => ({ Anchor: a.anchor || "(empty)", Backlinks: a.backlinksCount, "Ref. Domains": a.referringDomainsCount }))} />}>
          {anchors.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Anchor Text", "Backlinks", "Referring Domains"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {anchors.slice(0, 10).map((a: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{a.anchor || "(empty)"}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#FF9A4A" }}>{(a.backlinksCount ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#8B7BF7" }}>{(a.referringDomainsCount ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No anchor text data yet" description="Run an Authority & Trust audit to see your anchor text distribution." />
          )}
        </DataPanel>
      </div>
    );
  }

  // ─── DEFAULT SUB-NAV: Full Audit ───
  const keywords = liveData?.keywords ?? [];
  const techIssues = liveData?.technicalSEO?.issues ?? [];
  const dm = liveData?.domainMetrics;

  return (
    <div className="space-y-6">
      <DataPanel title="Keyword Performance" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Keyword Performance" data={flattenKeywords(keywords)} />}>
        {keywords.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Position", "Volume", "Difficulty", "Traffic Potential"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keywords.slice(0, 10).map((kw: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{kw.keyword}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>#{kw.position}</td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{(kw.searchVolume ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.min(kw.difficulty ?? 0, 100)}%`, background: (kw.difficulty ?? 0) > 60 ? "#F87171" : (kw.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                          </div>
                          <span className="text-xs" style={{ color: "#9AA0B3" }}>{kw.difficulty ?? 0}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-2"><span className="font-bold" style={{ color: "#FF9A4A" }}>{(kw.trafficPotential ?? 0).toLocaleString()}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(keywords.length, 10)} of <span style={{ color: "#FF9A4A" }}>{keywords.length}</span> keywords
            </div>
          </>
        ) : (
          <EmptyState title="No keyword data yet" description="Run a Full SEO Audit to see your keyword performance." icon={<BarChart3 className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
        )}
      </DataPanel>

      <ActionPlanIssuesPanel
        cardId={cardId}
        title="Technical Issues"
        fallback={
          <DataPanel title="Technical Issues" icon={<AlertTriangle className="w-4 h-4" />} actions={<WidgetExportBtn title="Technical Issues" data={flattenTechnicalIssues(techIssues)} />}>
            {techIssues.length > 0 ? (
              <div className="space-y-3">
                {techIssues.map((issue: any, i: number) => {
                  const sev = issue.severity === "critical" ? "HIGH" : issue.severity === "warning" ? "MED" : "LOW";
                  const colors = severityColor(sev);
                  return (
                    <div key={i} className="flex items-start justify-between gap-3 rounded-xl px-4 py-3"
                      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors.text }} />
                        <div className="text-sm font-medium" style={{ color: "#E8E4FF" }}>{issue.message}</div>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full flex-shrink-0 font-medium"
                        style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                        {sev}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="No technical data yet" description="Run a Full SEO Audit to see issues and recommendations." icon={<AlertTriangle className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
            )}
          </DataPanel>
        }
      />

      <DataPanel title="Domain Overview" icon={<TrendingUp className="w-4 h-4" />} actions={<WidgetExportBtn title="Domain Overview" data={dm ? flattenMetrics(dm) : []} />}>
        {dm ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <MetricCard label="Authority Score" value={`${dm.authorityScore}/100`} highlight />
            <MetricCard label="Organic Traffic" value={`${(dm.organicTraffic ?? 0).toLocaleString()}/mo`} />
            <MetricCard label="Organic Keywords" value={(dm.organicKeywords ?? 0).toLocaleString()} />
            <MetricCard label="Backlinks" value={(dm.backlinks ?? 0).toLocaleString()} />
            <MetricCard label="Referring Domains" value={(dm.referringDomains ?? 0).toLocaleString()} />
            <MetricCard label="Technical Score" value={liveData?.technicalSEO ? `${liveData.technicalSEO.score}/100` : "—"} />
          </div>
        ) : (
          <EmptyState title="No domain data yet" description="Run a Full SEO Audit to see your domain metrics." icon={<TrendingUp className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
        )}
      </DataPanel>
    </div>
  );
};

export default OverviewTabContent;
